import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useStores } from "../../../../stores/setup/use-store";
import { StoreState } from "../../../../enum/StoreState";
import { ILocationMenuItem, ILocationMenuInsight } from "../../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { UseEventManagerDataReturn } from "../types";

/**
 * Hook for managing Event Manager data including:
 * - Dashboard selection
 * - Locations and events fetching via menu-list endpoint
 */
export const useEventManagerData = (): UseEventManagerDataReturn => {
    const { t } = useTranslation("translation", {
        keyPrefix: "oceanWeatherIntelligence.insightAlert.eventManager",
    });

    const {
        dataStores: {
            operationRuleStore,
            insightAlertStore,
        },
    } = useStores();

    // Get initial dashboard ID from store (only on first render)
    const initialDashboardIdRef = useRef<number>(operationRuleStore.operationRuleMainId || 0);

    // Track if we've already initialized the dashboard from store
    const hasInitializedFromStoreRef = useRef<boolean>(initialDashboardIdRef.current > 0);

    // Track selected dashboard - completely local state, independent from store
    const [selectedDashboardId, setSelectedDashboardIdState] = useState<number>(
        initialDashboardIdRef.current
    );

    // Local copy of menu list to allow updates without refetching
    const [localMenuList, setLocalMenuList] = useState<ILocationMenuItem[]>([]);

    // Track if initial data has been loaded (to show loading instead of empty state on first load)
    const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);

    // Track if we just did a local update (to skip store sync)
    const skipNextSyncRef = useRef(false);

    // Track the dashboard ID that was last loaded to avoid unnecessary refetches
    const lastLoadedDashboardIdRef = useRef<number | null>(null);

    // Wrapper to set dashboard ID (does NOT sync back to store to avoid infinite loops)
    const setSelectedDashboardId = useCallback((dashboardId: number) => {
        if (dashboardId > 0) {
            setSelectedDashboardIdState(dashboardId);
        }
    }, []);

    // Initialize selectedDashboardId when store loads the favorite dashboard
    // This handles the case when the component mounts before dashboards are loaded
    useEffect(() => {
        if (!hasInitializedFromStoreRef.current && operationRuleStore.operationRuleMainId > 0) {
            hasInitializedFromStoreRef.current = true;
            setSelectedDashboardIdState(operationRuleStore.operationRuleMainId);
        }
    }, [operationRuleStore.operationRuleMainId]);

    // Fetch location menu list when dashboard changes
    // Only fetch if the dashboard ID actually changed
    useEffect(() => {
        if (selectedDashboardId > 0 && selectedDashboardId !== lastLoadedDashboardIdRef.current) {
            lastLoadedDashboardIdRef.current = selectedDashboardId;
            insightAlertStore.getLocationMenuListAPI(selectedDashboardId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDashboardId]);

    // Sync local menu list with store (skip if we just did a local update)
    useEffect(() => {
        if (skipNextSyncRef.current) {
            skipNextSyncRef.current = false;
            return;
        }
        if (insightAlertStore.locationMenuList.length > 0) {
            setLocalMenuList(insightAlertStore.locationMenuList);
        }
    }, [insightAlertStore.locationMenuList]);

    // Mark initial data as loaded when the API call completes (DONE or ERROR)
    useEffect(() => {
        if (
            insightAlertStore.locationMenuListState === StoreState.DONE ||
            insightAlertStore.locationMenuListState === StoreState.ERROR
        ) {
            if (!hasLoadedInitialData && lastLoadedDashboardIdRef.current !== null) {
                setHasLoadedInitialData(true);
            }
        }
    }, [insightAlertStore.locationMenuListState, hasLoadedInitialData]);

    // Handle dashboard selection change
    const handleDashboardChange = useCallback((dashboardId: number) => {
        if (dashboardId > 0) {
            setSelectedDashboardId(dashboardId);
            // Also update store for other parts of the app (but we don't listen to it)
            operationRuleStore.setOperationRuleMain(dashboardId);
            // Reset initial data flag to show loading for new dashboard
            setHasLoadedInitialData(false);
            // Clear local menu list when dashboard changes
            setLocalMenuList([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSelectedDashboardId]);

    // Update events for a specific location (used after reordering)
    const updateEventsForLocation = useCallback((locationId: number, events: ILocationMenuInsight[]) => {
        // Mark to skip next store sync to prevent flickering
        skipNextSyncRef.current = true;

        setLocalMenuList(prev =>
            prev.map(location =>
                location.id === locationId
                    ? { ...location, items: events }
                    : location
            )
        );
    }, []);

    // Update location order (used after reordering locations)
    const updateLocationOrder = useCallback((newOrder: ILocationMenuItem[]) => {
        // Mark to skip next store sync to prevent flickering
        skipNextSyncRef.current = true;

        setLocalMenuList(newOrder);
    }, []);

    // Refresh data (used after creating new events)
    const refreshData = useCallback(() => {
        if (selectedDashboardId > 0) {
            insightAlertStore.getLocationMenuListAPI(selectedDashboardId);
        }
    }, [selectedDashboardId, insightAlertStore]);

    // Remove a location locally (without API refresh)
    const removeLocation = useCallback((locationId: number) => {
        skipNextSyncRef.current = true;
        setLocalMenuList(prev => prev.filter(location => location.id !== locationId));
    }, []);

    // Remove an event from a location locally (without API refresh)
    const removeEvent = useCallback((locationId: number, eventId: number) => {
        skipNextSyncRef.current = true;
        setLocalMenuList(prev =>
            prev.map(location =>
                location.id === locationId
                    ? { ...location, items: location.items.filter(item => item.id !== eventId) }
                    : location
            )
        );
    }, []);

    return {
        selectedDashboardId,
        setSelectedDashboardId: handleDashboardChange,
        locationMenuList: localMenuList,
        // Show loading when: API is fetching OR initial data hasn't been loaded yet (but dashboard is selected)
        isLoading:
            insightAlertStore.locationMenuListState === StoreState.PEDDING ||
            (!hasLoadedInitialData && selectedDashboardId > 0),
        error: insightAlertStore.locationMenuListState === StoreState.ERROR
            ? t("errorLoadingData")
            : null,
        updateEventsForLocation,
        updateLocationOrder,
        refreshData,
        removeLocation,
        removeEvent,
    };
};

export default useEventManagerData;
